import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Landmark, Headset, ShieldCheck, Check } from "lucide-react";
import Logo from "@/components/Logo";
import { startConsulta, registrarInteres, seleccionarOferta, resetOperation } from "@/lib/api";
import { toast } from "sonner";

const LEGEND = "Estas son alternativas orientativas; la aprobación final depende de la institución correspondiente.";

function OptionCard({ op, selected, onInteres }) {
  return (
    <div className="af-card p-6" data-testid={`resultado-op-${op.id_producto}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl flex items-center justify-center shrink-0"
            style={{ width: 48, height: 48, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
            <Landmark className="text-af-orange-core" size={24} />
          </div>
          <div>
            <div className="text-white font-bold text-lg leading-tight">{op.institucion}</div>
            <div className="text-white/60 text-sm">{op.producto}</div>
          </div>
        </div>
        <span className="text-[11px] font-semibold px-2 py-1 rounded-md"
          style={{ background: "rgba(232,93,4,0.15)", color: "var(--af-orange-bright)", border: "1px solid rgba(232,93,4,0.35)" }}>
          {op.modalidad}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5">
        <div className="rounded-lg p-3" style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
          <div className="text-white/50 text-xs">Enganche desde</div>
          <div className="text-white font-semibold">{op.enganche_minimo_pct != null ? `${op.enganche_minimo_pct}%` : "Por definir"}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
          <div className="text-white/50 text-xs">Plazos</div>
          <div className="text-white font-semibold">{op.plazos_meses ? `${op.plazos_meses} meses` : "Por definir"}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
          <div className="text-white/50 text-xs">Tasa</div>
          <div className="text-white font-semibold">{op.tasa || "Por definir"}</div>
        </div>
      </div>

      <button data-testid={`interes-btn-${op.id_producto}`} onClick={() => onInteres(op.id_producto)}
        disabled={selected} className="af-btn-primary w-full mt-5">
        {selected ? <><Check size={18} /> Registrado</> : "Me interesa esta opción"}
      </button>
    </div>
  );
}

export default function Resultados() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const started = React.useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    startConsulta()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "No pudimos cargar tus resultados en este momento."))
      .finally(() => setLoading(false));
  }, []);

  const onInteres = async (id) => {
    try {
      await registrarInteres(id);
      const r = await seleccionarOferta(id);
      setSelected((s) => [...s, id]);
      toast.success("¡Listo! Creamos tu trámite.");
      resetOperation();
      if (r?.tramite_id) navigate(`/tramite/${r.tramite_id}`);
      else navigate("/mis-tramites");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos registrar tu interés. Intenta de nuevo.");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--af-black)" }}>
      <div className="sticky top-0 z-40" style={{ background: "rgba(13,13,13,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--af-border-dark)" }}>
        <div className="af-container flex items-center gap-3" style={{ height: 68 }}>
          <button data-testid="resultados-back" onClick={() => navigate("/")} className="text-white/80 hover:text-af-orange-core"><ArrowLeft size={22} /></button>
          <Link to="/"><Logo height={40} /></Link>
        </div>
      </div>

      <div className="af-container py-10 md:py-14 max-w-3xl" data-testid="resultados-page">
        {loading && (
          <div className="af-card p-12 text-center">
            <Loader2 className="animate-spin text-af-orange-core mx-auto mb-4" size={40} />
            <p className="text-white/70">Analizando tu perfil y comparando alternativas…</p>
          </div>
        )}

        {!loading && error && (
          <div className="af-card p-10 text-center" data-testid="resultados-error">
            <p className="text-white/80">{error}</p>
            <button onClick={() => navigate("/")} className="af-btn-primary mt-6">Volver al inicio</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.es_contado ? (
              <div className="af-card p-10 text-center" data-testid="resultado-contado">
                <div className="mx-auto rounded-full flex items-center justify-center mb-5" style={{ width: 64, height: 64, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
                  <Headset className="text-af-orange-core" size={30} />
                </div>
                <h1 className="h-impact text-white text-2xl md:text-3xl">Compra de contado</h1>
                <p className="text-white/75 mt-4 leading-relaxed">
                  Perfecto, para compra de contado uno de nuestros asesores te contactará en breve para ayudarte con el proceso.
                </p>
                <button onClick={() => navigate("/")} className="af-btn-primary mt-8">Volver al inicio</button>
              </div>
            ) : data.opciones && data.opciones.length > 0 ? (
              <>
                <div className="mb-8">
                  <div className="flex items-center gap-2 text-af-orange-core font-display uppercase tracking-widest text-sm mb-3">
                    <CheckCircle2 size={18} /> Tus resultados
                  </div>
                  <h1 className="h-impact text-white text-3xl md:text-4xl">Encontramos opciones que son compatibles preliminarmente con tu perfil.</h1>
                </div>
                <div className="space-y-5">
                  {data.opciones.map((op) => (
                    <OptionCard key={op.id_producto} op={op} selected={selected.includes(op.id_producto)} onInteres={onInteres} />
                  ))}
                </div>
                <div className="flex items-start gap-2 text-white/55 text-sm mt-6">
                  <ShieldCheck size={18} className="text-af-orange-core shrink-0 mt-0.5" />
                  <span>{LEGEND}</span>
                </div>
              </>
            ) : (
              <div className="af-card p-10 text-center" data-testid="resultado-revision">
                <div className="mx-auto rounded-full flex items-center justify-center mb-5" style={{ width: 64, height: 64, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
                  <Headset className="text-af-orange-core" size={30} />
                </div>
                <h1 className="h-impact text-white text-2xl md:text-3xl">Estamos revisando tu caso</h1>
                <p className="text-white/75 mt-4 leading-relaxed">
                  Necesitamos conocer un poco más de tu caso para encontrar la alternativa correcta para ti. Uno de nuestros asesores se pondrá en contacto contigo.
                </p>
                <button onClick={() => navigate("/")} className="af-btn-primary mt-8">Volver al inicio</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
